import React, { useEffect, useState } from "react";
import { toast } from "./ui/sonner";
import { Button } from "./ui/button";
import { Loader2, ArrowRight, X, History, RotateCcw, GitCompare } from "lucide-react";
import { fmtDT } from "../lib/date";

const money = (n) => "$" + Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
const SRC = { baseline: ["初始基线", "Baseline", "bg-slate-100 text-slate-600"], manual: ["手工", "Manual", "bg-blue-100 text-blue-700"], import: ["导入", "Import", "bg-amber-100 text-amber-700"], rollback: ["回滚", "Rollback", "bg-emerald-100 text-emerald-700"] };
const ACT = { price: ["改价", "Price", "bg-amber-100 text-amber-700"], added: ["新增", "Added", "bg-emerald-100 text-emerald-700"], disabled: ["停用", "Disabled", "bg-red-100 text-red-700"], restored: ["恢复", "Restored", "bg-blue-100 text-blue-700"], removed: ["移除", "Removed", "bg-red-100 text-red-700"], other: ["其他", "Other", "bg-slate-100 text-slate-600"] };
const fmt = (s) => fmtDT(s);

export const CatalogVersionsPanel = ({ zh, api, onClose, onRolledBack }) => {
  const [versions, setVersions] = useState([]);
  const [a, setA] = useState("");
  const [b, setB] = useState("current");
  const [cmp, setCmp] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");

  const load = () => api.get("/finance/payment-record/catalog/versions").then(({ data }) => { setVersions(data || []); if (!a && data?.length) setA(data[0].id); }).catch((e) => toast.error(e?.detailText || e.message));
  useEffect(() => { load(); }, []); // eslint-disable-line
  useEffect(() => {
    if (!a) return;
    setCmp(null);
    api.get("/finance/payment-record/catalog/versions/compare", { params: { a, b } }).then(({ data }) => setCmp(data)).catch((e) => toast.error(e?.detailText || e.message));
  }, [a, b]); // eslint-disable-line

  const rollback = async () => {
    const va = versions.find((v) => v.id === a);
    if (!va) return;
    if (!window.confirm(zh ? `回滚到 ${fmt(va.at)} 「${va.note}」？\n将恢复该版本的单价/启停状态（差异 ${cmp?.rows?.length || 0} 项），并写入改价历史与新版本。` : `Rollback to ${fmt(va.at)}?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/finance/payment-record/catalog/versions/${a}/rollback`, { note });
      toast.success(zh ? `已回滚：${data.changed} 项已恢复 ✓（新版本已保存）` : "Rolled back");
      setNote(""); setB("current"); load(); onRolledBack && onRolledBack();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  const label = (v) => `${fmt(v.at)} · ${zh ? SRC[v.source]?.[0] : SRC[v.source]?.[1]} · ${v.note || ""} (${v.by})`;

  return (
    <div className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50/30 p-3" data-testid="catalog-versions-panel">
      <div className="flex items-center gap-2 mb-2">
        <History className="h-4 w-4 text-emerald-600" />
        <span className="text-sm font-semibold text-slate-800">{zh ? "价目表版本 · 对比与回滚" : "Catalog versions"}</span>
        <span className="text-[11px] text-slate-500">{zh ? `${versions.length} 个版本（每次手工改动/导入/回滚自动存档）` : `${versions.length} versions`}</span>
        <button onClick={onClose} className="ml-auto p-1 rounded hover:bg-white" data-testid="catalog-versions-close"><X className="h-4 w-4" /></button>
      </div>
      <div className="grid md:grid-cols-[1fr_auto_1fr] gap-2 items-center text-xs">
        <label className="text-[10px] text-slate-500">A · {zh ? "基准版本（可回滚到此）" : "Base version"}
          <select data-testid="catalog-versions-a" value={a} onChange={(e) => setA(e.target.value)} className="mt-0.5 w-full border border-slate-200 rounded-md px-2 py-1.5 bg-white text-xs">
            {versions.map((v) => <option key={v.id} value={v.id}>{label(v)}</option>)}
          </select></label>
        <GitCompare className="h-4 w-4 text-slate-400 hidden md:block mt-4" />
        <label className="text-[10px] text-slate-500">B · {zh ? "对比目标" : "Compare to"}
          <select data-testid="catalog-versions-b" value={b} onChange={(e) => setB(e.target.value)} className="mt-0.5 w-full border border-slate-200 rounded-md px-2 py-1.5 bg-white text-xs">
            <option value="current">{zh ? "当前价目表" : "Current"}</option>
            {versions.map((v) => <option key={v.id} value={v.id}>{label(v)}</option>)}
          </select></label>
      </div>
      <div className="mt-2 rounded-md border border-slate-200 bg-white max-h-[34vh] overflow-auto" data-testid="catalog-versions-diff">
        {!cmp ? <p className="p-3 text-xs text-slate-400 inline-flex items-center gap-1"><Loader2 className="h-3.5 w-3.5 animate-spin" />{zh ? "对比中…" : "Comparing…"}</p>
          : !cmp.rows.length ? <p className="p-3 text-xs text-slate-400" data-testid="catalog-versions-same">{zh ? "两个版本完全一致" : "No differences"}</p>
          : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-slate-50"><tr className="text-left text-[10px] text-slate-500"><th className="py-1.5 px-2">{zh ? "类型" : "Type"}</th><th className="py-1.5 px-2">{zh ? "名称" : "Name"}</th><th className="py-1.5 px-2 text-right">A → B</th><th className="py-1.5 px-2">{zh ? "阶段 / 分组" : "Phase / Group"}</th></tr></thead>
              <tbody>
                {cmp.rows.map((r) => (
                  <tr key={r.key + r.action} className="border-t border-slate-100" data-testid={`catalog-versions-row-${r.key}`}>
                    <td className="py-1 px-2"><span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${ACT[r.action]?.[2]}`}>{zh ? ACT[r.action]?.[0] : ACT[r.action]?.[1]}</span></td>
                    <td className="py-1 px-2 text-slate-700">{r.name}{r.changes && <span className="text-[9px] text-slate-400 ml-1">({r.changes.join(", ")})</span>}</td>
                    <td className="py-1 px-2 text-right whitespace-nowrap">{r.action === "price" ? <span><span className="line-through text-slate-400">{money(r.oldPrice)}</span> <ArrowRight className="inline h-3 w-3 text-amber-500" /> <b className="text-amber-700">{money(r.price)}</b></span> : money(r.price)}</td>
                    <td className="py-1 px-2 text-slate-500">{r.phaseKey} / {r.group}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>
      {cmp && (
        <div className="flex flex-wrap items-center gap-2 mt-2 text-xs">
          <span className="text-slate-500 mr-auto" data-testid="catalog-versions-counts">{Object.entries(cmp.counts || {}).map(([k, v]) => `${zh ? ACT[k]?.[0] : ACT[k]?.[1]} ${v}`).join(" · ") || (zh ? "无差异" : "—")}</span>
          {b === "current" && cmp.rows.length > 0 && (<>
            <input data-testid="catalog-versions-rollback-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder={zh ? "回滚备注（可选）" : "Note (optional)"} className="border border-slate-200 rounded-md px-2 py-1.5 w-48" />
            <Button size="sm" variant="outline" disabled={busy} onClick={rollback} data-testid="catalog-versions-rollback" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-1" />}{zh ? `回滚到 A（恢复 ${cmp.rows.length} 项）` : `Rollback to A (${cmp.rows.length})`}</Button>
          </>)}
        </div>
      )}
    </div>
  );
};
